/**
 * HowItWorks component - Educational methodology section
 * Explains the debt calculation process in clear, accessible terms
 */

import { Calculator, TrendingUp, Target, CheckCircle } from 'lucide-react'
import '../howItWorks.css'

const HowItWorks = () => {
  const steps = [
    {
      icon: Calculator,
      title: 'Enter Your Debt Information',
      description: 'Start by entering your current balance, interest rate (APR), and desired monthly payment. Our calculator accepts a wide range of values to accommodate various debt types including credit cards, personal loans, auto loans, and student loans.',
      tips: [
        'Find your APR on your latest statement',
        'Include only the principal balance (not fees)',
        'Choose a monthly payment you can realistically afford'
      ]
    },
    {
      icon: TrendingUp,
      title: 'We Calculate Your Payoff Timeline',
      description: 'Our advanced algorithm uses the industry-standard amortization formula to calculate exactly how long it will take to pay off your debt. We factor in compound interest, payment frequency, and provide accurate month-by-month projections.',
      tips: [
        'Calculations use compound interest (APR ÷ 12 months)',
        'Minimum payment must exceed monthly interest',
        'Results show total interest paid over the life of the debt'
      ]
    },
    {
      icon: Target,
      title: 'Compare Payment Strategies',
      description: 'Experiment with different monthly payment amounts to see how small increases can dramatically reduce both your payoff time and total interest paid. Our interactive calculator updates instantly as you adjust your inputs.',
      tips: [
        'Adding just $50/month can save thousands in interest',
        // 'See side-by-side comparisons of different strategies', // TODO: Add this feature later
        'View detailed month-by-month payment schedules'
      ]
    },
    {
      icon: CheckCircle,
      title: 'Create Your Action Plan',
      description: 'Armed with accurate projections, you can make informed decisions about your debt repayment strategy. Use our results to set realistic goals, budget effectively, and track your progress toward financial freedom.',
      tips: [
        // 'Print or save your payment schedule for reference', // TODO: Add this feature later
        'Set calendar reminders for payment milestones',
        'Revisit the calculator when circumstances change'
      ]
    }
  ]

  const methodology = {
    title: 'The Science Behind the Calculator',
    sections: [
      {
        heading: 'Amortization Formula',
        content: 'We use the standard amortization formula: M = P[r(1+r)^n]/[(1+r)^n-1], where M is the monthly payment, P is the principal balance, r is the monthly interest rate (APR ÷ 12), and n is the number of payments. This formula is used by banks and financial institutions worldwide to calculate loan payments.'
      },
      {
        heading: 'Interest Calculation',
        content: 'Interest is calculated monthly using compound interest. Each month, the remaining balance is multiplied by the monthly interest rate (your APR divided by 12). This interest is added to your balance, then your payment is applied. The earlier you pay, the less interest accumulates.'
      },
      {
        heading: 'Payment Application',
        content: 'Each payment is first applied to accrued interest, with the remainder going toward principal reduction. Early in repayment, most of your payment covers interest. As the balance decreases, more of each payment reduces principal, creating a snowball effect.'
      },
      {
        heading: 'Accuracy Guarantee',
        content: 'Our calculations match those used by major financial institutions. However, actual payoff times may vary slightly due to payment timing, fees, or rate changes. Always verify with your lender for official payoff amounts.'
      }
    ]
  }

  return (
    <section id="how-it-works" className="how-it-works-section">
      <div className="section-container">
        <div className="section-header">
          <h2>How Our Debt Calculator Works</h2>
          <p className="section-subtitle">
            Transparent, accurate calculations powered by proven financial formulas
          </p>
        </div>

        {/* Step-by-Step Process */}
        <div className="steps-grid">
          {steps.map((step, index) => (
            <div key={index} className="step-card">
              <div className="step-number">{index + 1}</div>
              <div className="step-icon">
                <step.icon size={32} />
              </div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
              <ul className="step-tips">
                {step.tips.map((tip, tipIndex) => (
                  <li key={tipIndex}>
                    <CheckCircle size={16} />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Methodology Deep Dive */}
        <div className="methodology-section">
          <h3 className="methodology-title">{methodology.title}</h3>
          <div className="methodology-grid">
            {methodology.sections.map((section, index) => (
              <div key={index} className="methodology-card">
                <h4>{section.heading}</h4>
                <p>{section.content}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Key Benefits */}
        <div className="benefits-callout">
          <h3>Why Use Our Calculator?</h3>
          <div className="benefits-list">
            <div className="benefit-item">
              <div className="benefit-icon">🎯</div>
              <div>
                <strong>100% Accurate</strong>
                <p>Uses the same formulas as banks and lenders</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">🔒</div>
              <div>
                <strong>Completely Private</strong>
                <p>All calculations happen in your browser—no data stored</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">⚡</div>
              <div>
                <strong>Instant Results</strong>
                <p>See your payoff timeline and interest costs immediately</p>
              </div>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">📊</div>
              <div>
                <strong>Detailed Breakdown</strong>
                <p>View month-by-month payment schedules and progress</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
